/**
 * Statistics Kingdom, surveyed: the causeway country, and the inside of all
 * five of its regions (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **This is the kingdom about what you did not measure, so the ground under it
 * is not solid.** Half of it is fen and foreshore — water you cannot step on,
 * sand the tide has been over — and everything anybody actually does here is
 * done on a causeway, a quay or a floor that somebody built. The observatory is
 * up on the one hill, because looking at data you *have* is the only part of
 * this kingdom that stands on rock; the court is a building with a door,
 * because an inference is a thing you have to walk into and argue.
 *
 * The randomness is the subject and never the implementation. Nothing below
 * `apps/web` may call `Math.random` (CLAUDE.md), and a map is authored rows of
 * characters — so the one kingdom that is *about* chance is the one drawn with
 * the least of it in the code.
 *
 * **Terrain is scenery and never a lock.** A fen is water with paths through
 * it, which is the easiest possible way to strand a concept;
 * `validateRegionMap` walks all six from their arrival tiles on every
 * `npm run content:validate`, and every concept is an ordinary card on the
 * kingdom page besides.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const KINGDOM = 'kingdom-statistics';

/* ========================================================== the causeway === */

/**
 * The kingdom: one causeway across it, the fen below, the hill above.
 *
 * The road runs the full width because it has to — there is no other dry way
 * from one side of this kingdom to the other. The works and the court stand
 * north of it on firm ground with their doors onto it; the fen and the shore
 * are reached by going down off it; and the observatory is up the one hill.
 */
const STATISTICS_CAUSEWAY: Town = {
  kingdomId: KINGDOM,
  caption: 'One causeway across the fen, the works and the court along it, the shore to the east, and the observatory up the only hill.',
  width: 26,
  height: 18,
  /* On firm ground beside the causeway, in the middle of the kingdom. */
  start: { x: 8, y: 8 },
  rows: [
    'ffffffffffffffffffffffffff',
    'f^^^^^^^^................f',
    'f^......^................f',
    'f^......^................f',
    'f^^^^#^^^................f',
    'f....#.......%%%%%..%%%%%f',
    'f....#.......%___%..%___%f',
    'f....#.......%___%..%___%f',
    'f....#......#%%#%%..%%#%%f',
    'f########################f',
    'f.........#........,,,...f',
    'f,,,,,,,,,#,,,,....,,,...f',
    'f~~~~~~~~~~~~....,,,,,,,,f',
    'f~~~~~~~~~~~~~~~~,,,,,,,,f',
    'f~~~~~~~~~~~~~~~~,,,,,,,,f',
    'f~~~~~~~~~~~~~~~~,,,,,,,,f',
    'f~~~~~~~~~~~~~~~~,,,,,,,,f',
    'ffffffffffffffffffffffffff',
  ],
  places: [
    { kind: 'road', name: 'The Causeway', emoji: '🗺️', x: 1, y: 9 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 8, y: 10 },
    { kind: 'region', regionId: 'region-data-observatory', x: 5, y: 4 },
    { kind: 'region', regionId: 'region-chance-fen', x: 10, y: 11 },
    { kind: 'region', regionId: 'region-sample-space-shore', x: 20, y: 12 },
    { kind: 'region', regionId: 'region-random-variable-works', x: 15, y: 8 },
    { kind: 'region', regionId: 'region-inference-court', x: 22, y: 8 },
    { kind: 'boss', bossId: 'boss-statistics-kingdom', x: 12, y: 8 },
  ],
};

/* =========================================================== the regions === */

/**
 * Data Observatory — instruments in three ranks.
 *
 * The pictures and plots on the first floor, the summaries on the second, and
 * the two displays that need a second variable — the scatter and the two-way
 * table — on the third, because that is genuinely a different instrument and
 * not a bigger version of the first one.
 */
const DATA_OBSERVATORY: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-data-observatory',
  caption: 'Three ranks of instruments: what a display shows, what a summary says, and the two that need a second column.',
  width: 19,
  height: 13,
  /* On the bottom floor, by the door. */
  start: { x: 9, y: 10 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%',
    '%_________________%',
    '%_________________%',
    '%_________________%',
    '%%%%_%%%%_%%%%_%%%%',
    '%_________________%',
    '%_________________%',
    '%_________________%',
    '%%%%_%%%%_%%%%_%%%%',
    '%_________________%',
    '%_________________%',
    '%_________________%',
    '%%%%%%%%%_%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'scaled-graphs', x: 3, y: 2 },
    { kind: 'concept', conceptId: 'line-plots', x: 9, y: 2 },
    { kind: 'concept', conceptId: 'data-displays', x: 15, y: 2 },
    { kind: 'concept', conceptId: 'mean-median-mode', x: 3, y: 6 },
    { kind: 'concept', conceptId: 'data-variability', x: 9, y: 6 },
    { kind: 'concept', conceptId: 'histograms', x: 15, y: 6 },
    { kind: 'concept', conceptId: 'scatter-plots', x: 5, y: 10 },
    { kind: 'concept', conceptId: 'two-way-tables', x: 13, y: 10 },
    { kind: 'road', name: 'The Hill Path', emoji: '📊', x: 9, y: 12 },
  ],
};

/**
 * Chance Fen — six pools, and nothing to stand on but the banks between them.
 *
 * You cannot walk on a pool, and that is the region: every concept here stands
 * on a bank *beside* an outcome rather than on one. The counting is out on the
 * west bank where the fen is still being laid out, and the distributions are on
 * the east, where the pools have been counted often enough to have a shape.
 */
const CHANCE_FEN: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-chance-fen',
  caption: 'Six pools and the banks between them: there is nowhere here to stand except beside an outcome.',
  width: 19,
  height: 13,
  /* On the wide bank across the middle of the fen. */
  start: { x: 9, y: 5 },
  rows: [
    'fffffffffffffffffff',
    'f.....,,....,,....f',
    'f.~~~~,,~~~~,,~~~.f',
    'f.~~~~,,~~~~,,~~~.f',
    'f.~~~~,,~~~~,,~~~.f',
    'f,,,,,,,,,,,,,,,,,f',
    'f,,,,,,,,,,,,,,,,,f',
    'f.~~~~,,~~~~,,~~~.f',
    'f.~~~~,,~~~~,,~~~.f',
    'f.~~~~,,~~~~,,~~~.f',
    'f.....,,....,,....f',
    'f.....,,.#..,,....f',
    'fffffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'basic-probability', x: 6, y: 3 },
    { kind: 'concept', conceptId: 'experimental-probability', x: 12, y: 3 },
    { kind: 'concept', conceptId: 'compound-probability', x: 6, y: 8 },
    { kind: 'concept', conceptId: 'probability-distributions', x: 12, y: 8 },
    { kind: 'concept', conceptId: 'combinatorics-intro', x: 3, y: 5 },
    { kind: 'concept', conceptId: 'counting-probability', x: 16, y: 6 },
    { kind: 'road', name: 'The Fen Path', emoji: '📊', x: 9, y: 11 },
  ],
};

/**
 * Sample Space Shore — the sand marked off into every square it could be.
 *
 * A sample space is every outcome laid out where you can see all of them at
 * once, so the shore is ruled into equal plots and the four along the top are
 * the four ways of asking about them. Bayes is in the lower half, which is
 * where you end up when you already know something and have to go back.
 */
const SAMPLE_SPACE_SHORE: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-sample-space-shore',
  caption: 'Sand ruled off into equal plots: every outcome there is, laid out where you can see all of them at once.',
  width: 18,
  height: 12,
  /* On the sand in the lower half, with the whole grid in front of you. */
  start: { x: 6, y: 7 },
  rows: [
    'ffffffffffffffffff',
    'f................f',
    'f,,,#,,,#,,,#,,,,f',
    'f,,,#,,,#,,,#,,,,f',
    'f,,,#,,,#,,,#,,,,f',
    'f################f',
    'f,,,#,,,#,,,#,,,,f',
    'f,,,#,,,#,,,#,,,,f',
    'f,,,#,,,#,,,#,,,,f',
    'f~~~~~~~,~~~~~~~~f',
    'f~~~~~~~,~~~~~~~~f',
    'f~~~~~~~~~~~~~~~~f',
  ],
  places: [
    { kind: 'concept', conceptId: 'sample-spaces', x: 2, y: 3 },
    { kind: 'concept', conceptId: 'probability-events', x: 6, y: 3 },
    { kind: 'concept', conceptId: 'conditional-probability', x: 10, y: 3 },
    { kind: 'concept', conceptId: 'independence', x: 14, y: 3 },
    { kind: 'concept', conceptId: 'bayes-theorem', x: 10, y: 7 },
    { kind: 'road', name: 'The Strand', emoji: '📊', x: 8, y: 10 },
  ],
};

/**
 * Random Variable Works — benches at the top, the named engines below.
 *
 * The three bays along the top are what a random variable *is* and what you ask
 * of one; the open floor in the middle is the spread; and the long shop at the
 * bottom holds the four distributions that have names of their own, in a row,
 * because they are four machines rather than four ideas.
 */
const RANDOM_VARIABLE_WORKS: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-random-variable-works',
  caption: 'Three bays of benches, an open floor for the spread, and the long shop where the four named distributions stand in a row.',
  width: 20,
  height: 15,
  /* On the shop floor at the bottom, inside the door. */
  start: { x: 10, y: 13 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%%',
    '%______%_____%_____%',
    '%______%_____%_____%',
    '%__________________%',
    '%______%_____%_____%',
    '%%%%_%%%%%_%%%%%_%%%',
    '%__________________%',
    '%__________________%',
    '%__________________%',
    '%%%%_%%%%%_%%%%%_%%%',
    '%__________________%',
    '%__________________%',
    '%__________________%',
    '%__________________%',
    '%%%%%%%%%%_%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'discrete-random-variables', x: 3, y: 2 },
    { kind: 'concept', conceptId: 'continuous-random-variables', x: 10, y: 2 },
    { kind: 'concept', conceptId: 'expected-value', x: 16, y: 2 },
    { kind: 'concept', conceptId: 'random-variable-variance', x: 3, y: 7 },
    { kind: 'concept', conceptId: 'random-variable-sd', x: 9, y: 7 },
    { kind: 'concept', conceptId: 'normal-random-variable', x: 16, y: 7 },
    { kind: 'concept', conceptId: 'binomial-distribution', x: 3, y: 11 },
    { kind: 'concept', conceptId: 'geometric-distribution', x: 7, y: 11 },
    { kind: 'concept', conceptId: 'poisson-distribution', x: 11, y: 11 },
    { kind: 'concept', conceptId: 'normal-distribution', x: 16, y: 11 },
    { kind: 'road', name: 'The Works Door', emoji: '📊', x: 10, y: 14 },
  ],
};

/**
 * Inference Court — a bench, a floor, and two tables at the back.
 *
 * The only building in the kingdom you argue in. The sample and the sampling
 * distribution are up at the bench where the claim is made; the interval and
 * the test are on the floor below it, which is where a claim is answered; and
 * the two tables at the back are the correlation and the regression, off to one
 * side, because a line through a scatter is evidence and not a verdict.
 */
const INFERENCE_COURT: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-inference-court',
  caption: 'The bench where a claim is made, the floor where it is answered, and two tables at the back for the evidence.',
  width: 19,
  height: 14,
  /* On the floor of the court, facing the bench. */
  start: { x: 9, y: 12 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%',
    '%_________________%',
    '%___%_________%___%',
    '%___%%%%%_%%%%%___%',
    '%_________________%',
    '%_________________%',
    '%%%%%_%%%%%%%_%%%%%',
    '%_________________%',
    '%__%%_%%___%%_%%__%',
    '%__%___%___%___%__%',
    '%__%%%%%___%%%%%__%',
    '%_________________%',
    '%_________________%',
    '%%%%%%%%%_%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'sampling', x: 6, y: 2 },
    { kind: 'concept', conceptId: 'sampling-distributions', x: 12, y: 2 },
    { kind: 'concept', conceptId: 'standard-deviation', x: 3, y: 4 },
    { kind: 'concept', conceptId: 'confidence-intervals', x: 9, y: 5 },
    { kind: 'concept', conceptId: 'hypothesis-testing', x: 15, y: 4 },
    { kind: 'concept', conceptId: 'correlation', x: 5, y: 9 },
    { kind: 'concept', conceptId: 'regression', x: 13, y: 9 },
    { kind: 'concept', conceptId: 'linear-regression', x: 9, y: 11 },
    { kind: 'road', name: 'The Court Door', emoji: '📊', x: 9, y: 13 },
  ],
};

/* -------------------------------------------------------------------------- */

export const STATISTICS_TOWN: Town = STATISTICS_CAUSEWAY;

export const STATISTICS_REGION_MAPS: readonly RegionMap[] = [
  DATA_OBSERVATORY,
  CHANCE_FEN,
  SAMPLE_SPACE_SHORE,
  RANDOM_VARIABLE_WORKS,
  INFERENCE_COURT,
];
